/**
 * 货架几何的**共享领域规则**（`REQ-SHELF-002` ~ `REQ-SHELF-004`）。
 *
 * 为什么放在 `@cuito/types` 而不是两端各写一份：
 *   - 服务端（`apps/api` 的 `ShelfService`）用它做权威判定（409 `SHELF_OVERLAP`）；
 *   - 客户端（`apps/tenant` 的合并选择器，经 `@cuito/ui` 的 `shelfGrid` re-export）
 *     用它做提交前的即时反馈。
 * `REQ-SHELF-004`（`01-requirements.md:2002`）与契约覆盖矩阵
 * （`04-api-contract.md:3656`）都要求两端**同款逻辑** —— 两处各写一份，
 * 迟早会出现「客户端说能合并、服务端报 409」这种无法复现的投诉。
 *
 * 边界（不要越界）：**几何判定**属于共享领域规则（本文件）；
 * **像素布局**（`shelfRect` / `computeGridMetrics` / `defaultRowLabels`）只服务
 * 前端渲染，留在 `@cuito/ui` 的 `shelfGrid.ts`。
 */
/**
 * 判断两个矩形区域是否相交。
 *
 * 用**半开区间**比较（左闭右开）：`A1`（行 1、列 1）与 `A2`（行 1、列 2，紧邻）
 * 的右边界恰好等于对方的左边界，因此**相邻不算相交** —— 这正是「相邻格子可以
 * 合并、但不能与已占用的格子重叠」的语义（`REQ-SHELF-002` / `REQ-SHELF-004`）。
 */
export function regionsOverlap(a, b) {
    const aRight = a.colStart + a.colSpan;
    const aBottom = a.rowStart + a.rowSpan;
    const bRight = b.colStart + b.colSpan;
    const bBottom = b.rowStart + b.rowSpan;
    return a.colStart < bRight && b.colStart < aRight && a.rowStart < bBottom && b.rowStart < aBottom;
}
/**
 * 该区域是否**完整包住**了另一个 **1×1** 货架。
 *
 * 这是契约 §7.2 副作用（`04-api-contract.md:997` / `:1077-1081`）的几何判据：
 * 新建/调整货架时，「新区域完整包住原有 1×1 货架」要走**合并迁移并删除原货架**
 * （201，`ShelfItem` / `ShelfIcon` 保留），而不是 409 `SHELF_OVERLAP`；
 * 只有**交叉**才 409。
 *
 * ## 为什么放在契约层，而不是某一端
 *
 * 与 `regionsOverlap` 完全同一个理由（见文件头）：这是**契约原文的可执行版本**，
 * 不是任何一端的实现细节。
 *   - 服务端（`apps/api` 的 `ShelvesService`）用它把冲突货架分流成
 *     `absorbed`（迁移）与 `blocking`（409）；
 *   - 客户端（`apps/tenant` 的合并选择器）用它做提交前的即时反馈。
 * 两处各写一份的代价是**两种坏结果，而且两种都不可见**：
 *   - 判严了（把「包含」当冲突）→ 合并功能整体不可用，用户只能先删货架再建，
 *     连带丢掉格子里的 `ShelfItem` 与 `ShelfIcon`（契约明说这些应当迁移保留）；
 *   - 判松了（把「交叉」当合并）→ 按钮放行一个必然 409 的请求，用户白填一遍表单。
 * 本轮 S1（High）「客户端判据与服务端语义不一致 → 功能不可用」正是第一种。
 * 它**之所以能被发现**，靠的是有人恰好把两份实现逐条件比对了一遍 —— 这不是
 * 可以依赖的机制。唯一的实现放在契约层后，两边 import 的是同一个函数对象，
 * 分叉在构造上不可能发生。
 *
 * ## 四个条件缺一不可
 *
 * `rowSpan === 1 && colSpan === 1` 是**契约的边界**：§7.2 只对 1×1 说「迁移并删除」，
 * 2×2 及以上**没有任何吞并语义**（`packages/types/src/shelf.spec.ts` 的
 * ③ 号边界用例钉住这一点）—— 少这个条件就会静默吞掉用户 2×2 货架上的商品摆放。
 * 另外四边比较用的是**闭区间**：`rowStart + rowSpan - 1` 是区域的最后一行，
 * 「恰好落在边界上」必须算包住（② 号用例），这与 `regionsOverlap` 的半开区间
 * 是两套相邻语义，不能互相套用。
 */
export function coversUnitShelf(region, shelf) {
    return (shelf.rowSpan === 1 &&
        shelf.colSpan === 1 &&
        shelf.rowStart >= region.rowStart &&
        shelf.rowStart <= region.rowStart + region.rowSpan - 1 &&
        shelf.colStart >= region.colStart &&
        shelf.colStart <= region.colStart + region.colSpan - 1);
}
//# sourceMappingURL=shelf.js.map